(ns common.session-timeout
  "Shared session timeout options and utility functions.
   Used by both admin tenant settings and user profile settings.

   Values are stored in the database as keywords (e.g., :session-timeout/h2).
   This eliminates ambiguity between 'not set' (nil) and 'unlimited' (:session-timeout/unlimited).")

(def ^:private timeout-options-base
  "Base timeout options with value (keyword) and label"
  [{:value :session-timeout/m5        :label "5 minutes"}
   {:value :session-timeout/m15       :label "15 minutes"}
   {:value :session-timeout/m30       :label "30 minutes"}
   {:value :session-timeout/m45       :label "45 minutes"}
   {:value :session-timeout/h1        :label "1 hour"}
   {:value :session-timeout/h2        :label "2 hours"}
   {:value :session-timeout/h8        :label "8 hours"}
   {:value :session-timeout/h12       :label "12 hours"}
   {:value :session-timeout/h24       :label "24 hours"}
   {:value :session-timeout/h48       :label "48 hours"}
   {:value :session-timeout/d3        :label "3 days"}
   {:value :session-timeout/d7        :label "7 days"}
   {:value :session-timeout/d30       :label "30 days"}
   {:value :session-timeout/unlimited :label "Unlimited"}])

(def timeout-options
  "Session timeout options. Ordered from shortest to longest.
   :key/:value is the keyword stored in the database.
   :label is for display (use i18n lookup with :key in frontend)."
  (mapv #(assoc % :key (:value %)) timeout-options-base))

(def timeout-keys
  "Set of valid timeout keywords"
  (set (map :value timeout-options-base)))

(def ^:private key->minutes
  "Lookup map from keyword to minutes.
   Unlimited is represented as ~200 years in minutes — a concrete large number
   so that nil unambiguously means 'not configured'."
  {:session-timeout/m5        5
   :session-timeout/m15       15
   :session-timeout/m30       30
   :session-timeout/m45       45
   :session-timeout/h1        60
   :session-timeout/h2        120
   :session-timeout/h8        480
   :session-timeout/h12       720
   :session-timeout/h24       1440
   :session-timeout/h48       2880
   :session-timeout/d3        4320
   :session-timeout/d7        10080
   :session-timeout/d30       43200
   :session-timeout/unlimited (* 60 24 365 200)})

(def ^:private minutes->key
  "Lookup map from minutes to keyword (for migration).
   nil maps to 15 minutes as a safe default."
  {5     :session-timeout/m5
   15    :session-timeout/m15
   30    :session-timeout/m30
   45    :session-timeout/m45
   60    :session-timeout/h1
   120   :session-timeout/h2
   480   :session-timeout/h8
   720   :session-timeout/h12
   1440  :session-timeout/h24
   2880  :session-timeout/h48
   4320  :session-timeout/d3
   10080 :session-timeout/d7
   43200 :session-timeout/d30
   nil   :session-timeout/m15})

(defn timeout->minutes
  "Convert a timeout keyword to minutes.
   Returns nil if timeout-key is nil (not configured).
   Returns a large number for :session-timeout/unlimited."
  [timeout-key]
  (get key->minutes timeout-key))

(defn valid-timeout-key?
  "Check if a timeout keyword is valid"
  [timeout-key]
  (contains? timeout-keys timeout-key))

(defn round-down-minutes-to-key
  [minutes]
  (cond
    (nil? minutes) :session-timeout/m15
    (>= minutes 43200) :session-timeout/d30
    :else (or (->> timeout-options-base
                   (map #(assoc % :minutes (key->minutes (:value %))))
                   (filter #(not= :session-timeout/unlimited (:value %)))
                   (filter #(<= (:minutes %) minutes))
                   last
                   :value)
              :session-timeout/m5)))

(defn filter-options-by-max
  "Filter timeout options to only include those <= max timeout keyword.
   If max-timeout-key is nil (not configured), returns all options.
   Used to limit user options based on tenant admin setting."
  [max-timeout-key]
  (let [max-minutes (timeout->minutes max-timeout-key)]
    (if (nil? max-minutes)
      ;; nil means not configured - show all options
      timeout-options
      (filterv (fn [{:keys [value]}]
                 (let [mins (timeout->minutes value)]
                   (and (some? mins) (<= mins max-minutes))))
               timeout-options))))

(defn normalize-timeout
  "Normalize a timeout value to a valid keyword.
   - If already a valid keyword, return as-is
   - If a number, convert to nearest valid keyword (for migration)
   - If nil, return nil (not set)
   - Otherwise, throws validation error"
  [value]
  (cond
    (nil? value) nil
    (valid-timeout-key? value) value
    (number? value) (round-down-minutes-to-key value)
    :else (throw (ex-info "Invalid session timeout value"
                          {:error/type :error.type/validation-error
                           :value      value}))))

(defn cap-user-timeout
  "Cap user timeout to not exceed tenant maximum.
   Returns nil if user-timeout-key is nil (use tenant default).
   Returns tenant-max-key if user's timeout exceeds it."
  [user-timeout-key tenant-max-key]
  (cond
    (nil? user-timeout-key) nil
    (nil? tenant-max-key) user-timeout-key
    :else
    (let [user-minutes (timeout->minutes user-timeout-key)
          tenant-max-minutes (timeout->minutes tenant-max-key)]
      (if (<= user-minutes tenant-max-minutes)
        user-timeout-key
        tenant-max-key))))
